import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useNavigate } from "react-router-dom"

import * as Yup from "yup"
import { Grid } from "@mui/material"
import { Button, InputText, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import { FormikProvider, useFormik, useFormikContext } from "formik"
import { t } from "i18next"

import { Divider } from "../../../components/core/Divider"
import { SidAPI } from "../../../services/SidAPI"
import type { SendEmailReset } from "../../../types/generated/authentication/SendEmailReset"
import type { ForgotPasswordFormikValues, ForgotPasswordProps } from "../interfaces"
import { EntryDisclaimer } from "./EntryDisclaimer"

import styles from "../Auth.module.scss"

/**
 * ForgotPassword component that renders the password reset form and handles reset states
 *
 * @param {boolean} isResetPasswordSuccess - Indicates if password reset email was sent successfully
 * @param {boolean} isResetPasswordError - Indicates if password reset attempt failed
 * @returns {ReactElement} Password reset form with status messages
 */
export function ForgotPassword(props: Readonly<ForgotPasswordProps>): ReactElement {
  const { isResetPasswordSuccess, isResetPasswordError } = props

  const { t } = useTranslation("auth")

  const { values, errors, touched, isSubmitting, isValid, dirty, handleChange, handleBlur, submitForm } =
    useFormikContext<ForgotPasswordFormikValues>()

  const navigate = useNavigate()

  return (
    <Grid item container className={styles.mainContainer}>
      {/**
       * Forgot password snackbars
       * 1. email sent to account successfully
       * 2. Network error
       */}
      {isResetPasswordSuccess && <Snackbar severity="success" fullWidth description={t("forgotPassword.success")} />}

      {isResetPasswordError && <Snackbar severity="error" fullWidth description={t("forgotPassword.networkError")} />}

      <Grid container className={styles.formContainer}>
        <Grid item container xs={12} justifyContent="start">
          <Typography variant="h1-bold">{t("forgotPassword.title")}</Typography>
        </Grid>

        <Grid item container xs={12} justifyContent="start" gap={2}>
          <Typography variant="p">{t("forgotPassword.description")}</Typography>

          <InputText
            fullWidth
            required
            hideDescription
            id="email"
            label={t("forgotPassword.email.label")}
            placeholder={t("forgotPassword.email.placeholder")}
            value={values.email}
            error={Boolean(errors.email) && touched.email && errors.email}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />
        </Grid>

        <Button
          fullWidth
          variant="primary"
          type="submit"
          onClick={() => submitForm()}
          disabled={isSubmitting || !isValid || !dirty}
          isLoading={isSubmitting}
        >
          {t("forgotPassword.submit")}
        </Button>

        <Divider text={t("forgotPassword.or")} mt={1.25} mb={1.25} />

        <Button
          fullWidth
          id="return-to-login"
          variant="secondary"
          type="submit"
          onClick={() => navigate("/login")}
          disabled={isSubmitting}
        >
          {t("forgotPassword.returnToLogin")}
        </Button>
      </Grid>

      <EntryDisclaimer type="signin" />
    </Grid>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  email: Yup.string()
    .email(t("forgotPassword.email.invalid", { ns: "auth" }))
    .required(t("forgotPassword.email.required", { ns: "auth" })),
})

/**
 * Container component that manages the password reset state and form submission logic.
 * Handles the password reset email flow.
 *
 * @returns {ReactElement} The password reset form wrapped with Formik provider
 */
export function ForgotPasswordContainer(): ReactElement {
  const resetPasswordMutation = useMutation<SendEmailReset, AxiosError, SendEmailReset>(SidAPI.resetPassword, {
    onError: (e) => {
      if (e.response?.data) {
        formik.setErrors(e.response.data)
      }
    },
  })

  // form control
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: validationSchema,
    onSubmit: async ({ email }) => {
      // this api call actually returns no content when successful, only status 204
      await resetPasswordMutation.mutateAsync({ email })
    },
  })

  return (
    <FormikProvider value={formik}>
      <ForgotPassword
        isResetPasswordSuccess={resetPasswordMutation.isSuccess}
        isResetPasswordError={resetPasswordMutation.isError}
      />
    </FormikProvider>
  )
}
